import { ReactNode } from 'react';
import { Provider } from 'react-redux';
import { SnackbarProvider } from 'notistack';
import { createRoot } from 'react-dom/client';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';
import materialUITheme from '@redact/materialUITheme';
import SnackbarCustom from '@common-components/Snackbar';
import configureAppStore from '@redact-state/configureStore';
import { helpers } from '@veritone/glc-redux';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import UserOnboardingRoot from '@user-onboarding/components/UserOnboardingRoot';
import UiFontSync from '@common-components/UiFontSync';
// Load external/global CSS
import '@resources/styles/veritone-icon.css';
import '@resources/styles/veritone-engines.css';
import '@resources/styles/common-global.scss';
import '@resources/styles/redact-global.scss';
import 'video-react/dist/video-react.css';

// The four non-Nunito faces the font dropdown can compare against, DELIBERATELY
// COMMENTED OUT. They are self-hosted, so no external fetch, and nothing
// renders in them while the dropdown is off — so no user ever downloads one.
// But webpack emits every .woff2 they reference either way, and that is 596KB
// of dead files in the build (Inter 213, IBM Plex 182, Source Sans 149, Public
// Sans 52). Not worth shipping to compare fonts the family choice already
// settled on.
//
// To compare them again: uncomment these AND set EXTRA_PREVIEW_FONTS_LOADED in
// fontPreview.ts. The packages stay in package.json so that is all it takes.
// The dropdown hides these four until both are done, because an unimported
// family falls back to the system sans and reads as a rendering bug.
//
// Nunito Sans (every width) and Roboto are NOT here — they ship anyway, from
// common-app/pages/App, so the dropdown always has those to compare.
// import '@fontsource-variable/inter/wght.css';
// import '@fontsource-variable/public-sans/wght.css';
// import '@fontsource-variable/source-sans-3/wght.css';
// import '@fontsource/ibm-plex-sans/latin-400.css';
// import '@fontsource/ibm-plex-sans/latin-500.css';
// import '@fontsource/ibm-plex-sans/latin-600.css';
// import '@fontsource/ibm-plex-sans/latin-700.css';
import { applyStoredFontPreview } from '@redact-components/CaseMedia/fontPreview';

// Re-apply the remembered choice before React paints, so a reload does not
// flash back to the shipping font.
applyStoredFontPreview();

const styleNode = document.createComment('jss-insertion-point');
if (document.head) {
  document.head.insertBefore(styleNode, document.head.firstChild);
}

const store = configureAppStore();

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;

const { handleImplicitRedirect } = helpers;

/* Bootstrap the necessary dependencies into your React app */
export function bootstrap(Application: ReactNode, element: string) {
  if (window.name === '_auth') {
    /* If this is an OAuth redirect window, deal with the OAuth response */
    return handleImplicitRedirect(window.location.hash, window.opener);
  }

  const container = document.getElementById(element);
  // TODO: Can we justify the use of ! here?
  const root = createRoot(container!);
  root.render(
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={materialUITheme}>
        <Provider store={store}>
          <SnackbarProvider
            autoHideDuration={5000}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <UiFontSync />
            {Application}
            <ToastContainer />
            <UserOnboardingRoot />
            <SnackbarCustom />
          </SnackbarProvider>
        </Provider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
